import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { bundleCheckoutRequestSchema, cardCheckoutSessionResponseSchema, cardOrderCheckoutRequestSchema } from '@inktap/contracts/customer-commerce';
import { customerOrderResponseSchema, customerOrdersResponseSchema, type CustomerOrder } from '@inktap/contracts/customer-orders';
import { request } from '../lib/api';
import { formatMoney } from '../lib/money';
import { useCardPlanCheckout } from '../lib/card-plan-checkout';
import { CardPlanSelection } from '../components/card/CardPlanSelection';
import { useSession } from '../session';
import { formatDate } from './portalTypes';
import { canReviewPendingOrder } from './orderEligibility';
import { OrderArtwork } from './OrderArtwork';
import './customer-orders-refresh.css';

const materialLabel: Record<CustomerOrder['material'], string> = {
  PVC: 'PVC card',
  METAL: 'Metal card',
  WOOD: 'Wood card'
};

const orderStateCopy: Record<CustomerOrder['status'], { title: string; detail: string }> = {
  PENDING: { title: 'Order pending', detail: 'Review your order and payment status above.' },
  PROCESSING: { title: 'In production', detail: 'Your order is confirmed and being prepared for print.' },
  SHIPPED: { title: 'Shipped', detail: 'Your card has been shipped.' },
  CANCELED: { title: 'Canceled', detail: 'This order will not be fulfilled.' }
};

const orderSteps: Array<{ status: CustomerOrder['status']; label: string }> = [
  { status: 'PENDING', label: 'Order created' },
  { status: 'PROCESSING', label: 'In production' },
  { status: 'SHIPPED', label: 'Shipped' }
];

const orderStepIndex: Record<CustomerOrder['status'], number> = {
  PENDING: 0,
  PROCESSING: 1,
  SHIPPED: 2,
  CANCELED: -1
};

const paymentLabels: Record<CustomerOrder['paymentState'], string> = {
  UNPAID: 'Unpaid', CHECKOUT_CREATING: 'Preparing checkout', CHECKOUT_OPEN: 'Checkout open',
  CANCELED: 'Canceled', PAID: 'Paid', FAILED: 'Failed', EXPIRED: 'Expired',
  PARTIALLY_REFUNDED: 'Partially refunded', REFUNDED: 'Refunded', DISPUTED: 'Disputed',
  CHARGEBACK: 'Chargeback', REVIEW_REQUIRED: 'Review required'
};

const reservationLabels: Record<NonNullable<CustomerOrder['reservation']>['state'], string> = {
  ACTIVE: 'Active', CONSUMED: 'Consumed', RELEASED: 'Released', EXPIRED: 'Expired'
};

const shipmentLabels: Record<NonNullable<CustomerOrder['shipment']>['status'], string> = {
  LABEL_CREATED: 'Label created', IN_TRANSIT: 'In transit', DELIVERED: 'Delivered',
  EXCEPTION: 'Delivery exception', RETURNED: 'Returned', CANCELED: 'Shipment canceled'
};

const safeTrackingUrl = (value: string | null): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
};

function OrdersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const cursor = new URLSearchParams(location.search).get('cursor');
  const scopeSearch = new URLSearchParams(location.search);
  scopeSearch.delete('cursor');
  const paginationScope = JSON.stringify([location.pathname, scopeSearch.toString(),
    session?.email, session?.slug, session?.expiresAt, session?.active]);
  // An opaque per-mount owner prevents history entries from another session or
  // filter context from supplying cursor navigation to this one.
  const paginationOwner = useRef<{ scope: string; id: string } | null>(null);
  if (!paginationOwner.current || paginationOwner.current.scope !== paginationScope) {
    paginationOwner.current = { scope: paginationScope,
      id: globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) };
  }
  const routeState = location.state && typeof location.state === 'object' && !Array.isArray(location.state)
    ? location.state as Record<string, unknown> : {};
  const savedPage = routeState.ordersPage;
  const saved = savedPage && typeof savedPage === 'object' && !Array.isArray(savedPage)
    ? savedPage as Record<string, unknown> : null;
  const previous = saved?.owner === paginationOwner.current.id && saved.cursor === cursor
    && Array.isArray(saved.previous) && saved.previous.length <= 1000
    && saved.previous.every((value: unknown) => value === null
      || (typeof value === 'string' && /^[A-Za-z0-9_-]{1,512}$/.test(value)))
    ? saved.previous as Array<string | null> : [];
  const contextKey = JSON.stringify([location.key, location.pathname, location.search,
    session?.email, session?.slug, session?.expiresAt, session?.active]);
  const context = useRef({ key: contextKey, generation: 0 });
  if (context.current.key !== contextKey) {
    context.current = { key: contextKey, generation: context.current.generation + 1 };
  }
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<{ scope: string; ids: Set<string> }>(() => ({ scope: contextKey, ids: new Set() }));
  const [previewEpoch, setPreviewEpoch] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutOrderId, setCheckoutOrderId] = useState<string | null>(null);
  const orderRequest = useRef(0);
  const checkoutRequest = useRef(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const previousLocation = useRef(location.key);
  const focusAfterLoad = useRef(false);
  const cardPlan = useCardPlanCheckout(true, contextKey);

  const loadOrders = useCallback(async () => {
    const version = ++orderRequest.current;
    const generation = context.current.generation;
    const valid = () => version === orderRequest.current && generation === context.current.generation;
    ++checkoutRequest.current;
    if (typeof document !== 'undefined' && document.activeElement?.closest('.order-list')) focusAfterLoad.current = true;
    setCheckoutOrderId(null);
    setLoading(true);
    setOrders([]);
    setNextCursor(null);
    setError(null);
    try {
      const path = cursor === null ? '/me/orders' : `/me/orders?cursor=${encodeURIComponent(cursor)}`;
      const payload = await request<unknown>(path, { cache: 'no-store' });
      if (!valid()) return;
      const parsed = customerOrdersResponseSchema.safeParse(payload);
      if (!parsed.success) throw new Error('Invalid order history');
      setOrders(parsed.data.orders);
      setExpandedOrders(current => current.scope === contextKey
        ? { scope: contextKey, ids: new Set([...current.ids].filter(id => parsed.data.orders.some(order => order.id === id))) }
        : { scope: contextKey, ids: new Set() });
      setNextCursor(parsed.data.nextCursor);
    } catch {
      if (valid()) setError('Unable to load your orders. Retry this page or return to your newest orders.');
    } finally {
      if (valid()) {
        setLoading(false);
        if (focusAfterLoad.current) {
          heading.current?.focus();
          focusAfterLoad.current = false;
        }
      }
    }
  }, [cursor, contextKey]);

  const changePage = (next: string | null, direction: 'newest' | 'previous' | 'older') => {
    if (loading || checkoutOrderId !== null) return;
    const search = new URLSearchParams(location.search);
    if (next === null) search.delete('cursor');
    else search.set('cursor', next);
    const visited = direction === 'older' ? [...previous, cursor].slice(-1000)
      : direction === 'previous' ? previous.slice(0, -1) : [];
    navigate({ pathname: location.pathname, search: search.toString() }, {
      state: { ...routeState, ordersPage: { owner: paginationOwner.current!.id, cursor: next, previous: visited } }
    });
  };

  const resumeCheckout = useCallback(async (orderId: string) => {
    if (loading || checkoutOrderId !== null) return;
    if (!session?.email) {
      setError('Sign in again to complete this order.');
      return;
    }
    const generation = context.current.generation;
    const version = ++checkoutRequest.current;
    const valid = () => generation === context.current.generation && version === checkoutRequest.current;
    const shown = orders.find((order) => order.id === orderId);
    if (!shown) return;
    setCheckoutOrderId(orderId);
    setError(null);
    let safeError = 'Unable to confirm this order. Reload your orders before continuing.';
    try {
      let current: CustomerOrder;
      try {
        const payload = await request<unknown>(`/me/orders/${encodeURIComponent(orderId)}`, { cache: 'no-store' });
        if (!valid()) return;
        const detail = customerOrderResponseSchema.safeParse(payload);
        if (!detail.success || detail.data.order.id !== orderId) throw new Error('Invalid selected order');
        current = detail.data.order;
      } catch {
        if (valid()) {
          if (typeof document !== 'undefined' && document.activeElement?.closest('[data-order-id]')?.getAttribute('data-order-id') === orderId) {
            heading.current?.focus();
          }
          setOrders((visible) => visible.filter((order) => order.id !== orderId));
          setExpandedOrders(current => {
            if (current.scope !== contextKey || !current.ids.has(orderId)) return current;
            const ids = new Set(current.ids);
            ids.delete(orderId);
            return { scope: current.scope, ids };
          });
        }
        throw new Error(safeError);
      }
      if (!valid()) return;
      setOrders((visible) => visible.map((order) => order.id === orderId ? current : order));
      if (current.status !== 'PENDING') {
        safeError = 'This order has changed and is no longer awaiting checkout.';
        throw new Error(safeError);
      }
      if (!canReviewPendingOrder(current)) {
        safeError = 'This order’s payment status changed. Review its refreshed status before continuing.';
        throw new Error(safeError);
      }
      if (current.totalPriceCents !== shown.totalPriceCents || current.currency !== shown.currency || current.quantity !== shown.quantity) {
        safeError = 'Your order details changed. Review the refreshed card amount before continuing.';
        throw new Error(safeError);
      }
      safeError = 'Unable to confirm subscription terms. Review the available plans before continuing.';
      let planCheckout;
      try {
        planCheckout = await cardPlan.reviewCurrent();
      } catch (err) {
        if (err instanceof Error && err.message === 'Your subscription terms changed. Review the refreshed plan before continuing.') {
          safeError = err.message;
        }
        throw err;
      }
      if (!valid()) return;
      safeError = 'Unable to confirm checkout. Reload your orders before trying again.';
      const body = (planCheckout.path === '/billing/bundle-checkout-session'
        ? bundleCheckoutRequestSchema
        : cardOrderCheckoutRequestSchema).safeParse({
        orderId, ...(planCheckout.path === '/billing/bundle-checkout-session' ? { email: session.email } : {}), ...planCheckout.fields
      });
      if (!body.success || !valid()) throw new Error(safeError);
      const response = await request<unknown>(planCheckout.path, {
        method: 'POST',
        body: JSON.stringify(body.data)
      });
      if (!valid()) return;
      const checkout = cardCheckoutSessionResponseSchema.safeParse(response);
      if (!checkout.success || !checkout.data.url) throw new Error(safeError);
      window.location.href = checkout.data.url;
    } catch (err: unknown) {
      if (!valid()) return;
      if (typeof err === 'object' && err !== null && 'status' in err && err.status === 409) await cardPlan.refresh();
      if (valid()) setError(safeError);
    } finally {
      if (valid()) setCheckoutOrderId(null);
    }
  }, [session?.email, orders, loading, checkoutOrderId, contextKey, cardPlan.reviewCurrent, cardPlan.refresh]);

  useEffect(() => {
    focusAfterLoad.current = previousLocation.current !== location.key;
    previousLocation.current = location.key;
    const refresh = () => { void loadOrders(); };
    // Another tab can change the shared workspace cookie while this tab is hidden.
    // Retire open previews and their requests; focus reloads the order list.
    const retirePreviews = () => {
      if (document.visibilityState === 'hidden') setPreviewEpoch(epoch => epoch + 1);
    };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    document.addEventListener('visibilitychange', retirePreviews);
    return () => {
      ++context.current.generation;
      ++orderRequest.current;
      ++checkoutRequest.current;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      document.removeEventListener('visibilitychange', retirePreviews);
    };
  }, [location.key, loadOrders]);

  const pagination = (position: 'top' | 'bottom') => (
    <nav className={`orders-pagination${position === 'bottom' ? ' orders-pagination--bottom' : ''}`}
      aria-label={position === 'top' ? 'Order history pages' : 'Order history pages, bottom'}>
      {position === 'top' ? <p role="status" aria-live="polite">{loading ? 'Loading order history…' : error ? 'Order history needs a refresh.' : `${orders.length} ${orders.length === 1 ? 'order' : 'orders'} on this page`}</p> : null}
      {cursor !== null || error ? <button type="button" className="btn-secondary" aria-label={position === 'bottom' ? 'Newest orders, bottom' : undefined} disabled={loading || checkoutOrderId !== null} onClick={() => changePage(null, 'newest')}>Newest orders</button> : null}
      {previous.length > 0 ? <button type="button" className="btn-secondary" aria-label={position === 'bottom' ? 'Previous page, bottom' : undefined} disabled={loading || checkoutOrderId !== null} onClick={() => changePage(previous[previous.length - 1], 'previous')}>Previous page</button> : null}
      {nextCursor ? <button type="button" className="btn-secondary" aria-label={position === 'bottom' ? 'Older orders, bottom' : undefined} disabled={loading || checkoutOrderId !== null} onClick={() => changePage(nextCursor, 'older')}>Older orders</button> : null}
    </nav>
  );

  return (
    <div className="orders-page">
      <header className="portal-page-header">
        <div>
          <h1 ref={heading} tabIndex={-1}>Your orders</h1>
          <p>Follow each card from checkout to delivery, all in one place.</p>
        </div>
        <Link className="btn-primary" to="/card-design">Design another card</Link>
      </header>

      {error ? <div className="alert" role="alert">{error}</div> : null}
      {error ? <button type="button" className="btn-secondary" disabled={loading || checkoutOrderId !== null} onClick={() => void loadOrders()}>Retry this page</button> : null}
      {pagination('top')}

      {loading ? (
        <section className="orders-loading" aria-live="polite"><p>Loading your orders…</p></section>
      ) : orders.length > 0 ? (
        <>
        <section className="order-list" aria-label="Card orders">
          <h2 className="orders-history-title">Order history</h2>
          {orders.map((order) => {
            const deliveryTracked = order.status === 'SHIPPED' && order.shipment !== null &&
              ['LABEL_CREATED', 'IN_TRANSIT', 'DELIVERED'].includes(order.shipment.status);
            const delivered = deliveryTracked && order.shipment?.status === 'DELIVERED';
            const shipmentNeedsAttention = order.shipment !== null && ['EXCEPTION', 'RETURNED', 'CANCELED'].includes(order.shipment.status);
            const status = delivered ? { title: 'Delivered', detail: 'Your delivery is confirmed.' } : orderStateCopy[order.status];
            const steps = deliveryTracked ? [...orderSteps, { status: 'DELIVERED' as const, label: 'Delivered' }] : orderSteps;
            const currentStep = delivered ? 3 : orderStepIndex[order.status];
            const trackingUrl = safeTrackingUrl(order.shipment?.trackingUrl ?? null);
            const expanded = expandedOrders.scope === contextKey && expandedOrders.ids.has(order.id);
            const detailId = `order-detail-${encodeURIComponent(order.id)}`;
            const reviewCheckout = canReviewPendingOrder(order);
            const reviewLabel = reviewCheckout ? 'Review checkout' : order.status === 'PENDING' ? 'Review payment status' : 'View details';

            return (
              <article className={`order-card order-card--${order.status.toLowerCase()}${deliveryTracked ? ' order-card--delivery' : ''}${shipmentNeedsAttention ? ' order-card--shipment-attention' : ''}`} key={order.id} data-order-id={order.id}>
                <div className="order-card__summary">
                  <div className="order-card__title">
                    <p>{order.styleId ? order.styleId.replace(/[-_]+/g, ' ') : materialLabel[order.material]}</p>
                    <h2>{materialLabel[order.material]}</h2>
                    <span>Quantity {order.quantity} · Ordered {formatDate(order.createdAt)}</span>
                    <span className="order-card__id">Order {order.id}</span>
                  </div>
                  <div className="order-card__status">
                    <strong>{status.title}</strong>
                    <span>Card amount: {formatMoney(order.totalPriceCents, order.currency)}</span>
                    <span>Payment: {paymentLabels[order.paymentState]}</span>
                    {order.shipment ? (
                      <span className={shipmentNeedsAttention ? 'order-card__shipment-alert' : undefined}>
                        Shipment: {order.shipment.status === 'CANCELED' ? 'Canceled' : shipmentLabels[order.shipment.status]}
                      </span>
                    ) : null}
                    {order.reservation ? (
                      <span>Reservation: {reservationLabels[order.reservation.state]}
                        {order.reservation.state === 'ACTIVE' ? ` · expires ${formatDate(order.reservation.expiresAt)}` : ''}
                      </span>
                    ) : null}
                  </div>
                  <button type="button" className="btn-secondary order-card__toggle"
                    aria-expanded={expanded} aria-controls={detailId}
                    aria-label={`${expanded ? 'Hide details' : reviewLabel} for order ${order.id}`}
                    disabled={checkoutOrderId !== null}
                    onClick={() => setExpandedOrders(current => {
                      const ids = current.scope === contextKey ? new Set(current.ids) : new Set<string>();
                      if (ids.has(order.id)) ids.delete(order.id); else ids.add(order.id);
                      return { scope: contextKey, ids };
                    })}>
                    {expanded ? 'Hide details' : reviewLabel}
                  </button>
                </div>

                <div id={detailId} className="order-card__detail" hidden={!expanded}>
                <div className="order-card__artwork-context">
                  <OrderArtwork key={JSON.stringify([contextKey, order.id, previewEpoch])} order={order} />
                  <span>Card artwork</span>
                </div>
                {order.status !== 'CANCELED' && (
                  <div className={`order-timeline${deliveryTracked ? ' order-timeline--delivery' : ''}`} aria-label={`Order status: ${status.title}`}>
                    {steps.map((step, index) => {
                      const complete = index <= currentStep;
                      const detail = step.status === 'DELIVERED'
                        ? delivered ? order.shipment?.deliveredAt ? formatDate(order.shipment.deliveredAt) : 'Confirmed' : 'Next'
                        : index === 0 ? formatDate(order.createdAt)
                          : index === orderStepIndex[order.status] ? formatDate(order.statusChangedAt)
                            : complete ? 'Completed' : 'Next';
                      return (
                        <div className={[complete && 'is-complete', index < currentStep && 'is-connected'].filter(Boolean).join(' ')} aria-current={index === currentStep ? 'step' : undefined} key={step.status}>
                          <span aria-hidden="true">{complete ? '✓' : ''}</span>
                          <strong>{step.label}</strong>
                          <small>{detail}</small>
                        </div>
                      );
                    })}
                  </div>
                )}

                {order.shipment ? (
                  <section className="order-card__shipment" aria-label={`Shipment for order ${order.id}`}>
                    <div><strong>{shipmentLabels[order.shipment.status]}</strong>
                      <span>{order.shipment.carrier}{order.shipment.service ? ` · ${order.shipment.service}` : ''}</span>
                      <span>Tracking: {order.shipment.trackingNumber}</span>
                      {order.shipment.deliveredAt ? <span>Delivered {formatDate(order.shipment.deliveredAt)}</span> : null}
                    </div>
                    {trackingUrl ? <a className="button-secondary" href={trackingUrl} target="_blank" rel="noopener noreferrer">Track shipment <span className="sr-only">(opens in a new tab)</span></a> : null}
                  </section>
                ) : null}

                <footer className="order-card__footer">
                  <p>{status.detail}</p>
                  {canReviewPendingOrder(order) ? (
                    <div className="order-card__actions">
                      <p>Stored card amount shown above. Review your order before continuing.</p>
                      <CardPlanSelection catalog={cardPlan.catalog} selectedPlan={cardPlan.selectedPlan}
                        onChange={cardPlan.setSelectedPlan} loading={cardPlan.loading} error={cardPlan.error}
                        disabled={checkoutOrderId !== null} onRefresh={() => void cardPlan.refresh()} />
                      <button type="button" className="btn-primary" onClick={() => void resumeCheckout(order.id)} disabled={loading || checkoutOrderId !== null || cardPlan.unavailable}>
                        {checkoutOrderId === order.id ? 'Opening checkout…' : 'Complete checkout'}
                      </button>
                      <Link className="btn-secondary" to={`/cards/design?order=${order.id}`}>Edit card</Link>
                    </div>
                  ) : order.status === 'PENDING' ? (
                    <div className="order-card__actions">
                      <p>{order.paymentState === 'CHECKOUT_CREATING'
                        ? 'Checkout is being prepared. Refresh to check its status.'
                        : 'Checkout and editing are unavailable for this payment status. Refresh to check for updates.'}</p>
                      <button type="button" className="btn-secondary" disabled={loading || checkoutOrderId !== null}
                        onClick={() => { focusAfterLoad.current = true; void loadOrders(); }}>Refresh order status</button>
                    </div>
                  ) : (
                    <Link className="btn-secondary" to="/card-design">Order again</Link>
                  )}
                </footer>
                </div>
              </article>
            );
          })}
        </section>
        {!error && (cursor !== null || previous.length > 0 || nextCursor !== null) ? pagination('bottom') : null}
        </>
      ) : error ? null : cursor !== null ? (
        <section className="orders-empty-state">
          <h2>No older orders on this page.</h2>
          <p>Return to your newest orders to review your history.</p>
        </section>
      ) : (
        <section className="orders-empty-state">
          <div className="orders-empty-state__mark" aria-hidden="true">⌁</div>
          <h2>Your first card starts here.</h2>
          <p>Choose a card, make it your own, and we’ll keep you posted from checkout through shipment.</p>
          <Link className="btn-primary" to="/card-design">Design your card</Link>
        </section>
      )}
    </div>
  );
}

export default OrdersPage;
